import {
	useResolveCommunicationRecipients,
	useSendCommunicationEmail,
} from "@features/communications/queries";
import { useStudentTeachers } from "../sub-features/student-teachers/queries";
import {
	Box,
	Button,
	Checkbox,
	Divider,
	Group,
	ScrollArea,
	Stack,
	Tabs,
	Text,
	TextInput,
} from "@mantine/core";
import { RichTextEditor, Link } from "@mantine/tiptap";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { notifications } from "@mantine/notifications";
import { useMemo, useState } from "react";
import classes from "./SendConnectedAdultsEmailModal.module.css";

export function SendConnectedAdultsEmailModal({
	studentNo,
	onClose,
}: {
	studentNo: string;
	onClose: () => void;
}) {
	const { data: studentTeachers } = useStudentTeachers(studentNo);
	const collegeResolution = useResolveCommunicationRecipients({
		students: [`student:${studentNo}`],
		to: ["selected-students:college"],
		cc: [],
		bcc: [],
	});
	const personalResolution = useResolveCommunicationRecipients({
		students: [`student:${studentNo}`],
		to: ["selected-students:personal"],
		cc: [],
		bcc: [],
	});
	const teacherResolution = useResolveCommunicationRecipients({
		students: [`student:${studentNo}`],
		to: ["selected-students:teachers"],
		cc: [],
		bcc: [],
	});
	const parentResolution = useResolveCommunicationRecipients({
		students: [`student:${studentNo}`],
		to: ["selected-students:parents"],
		cc: [],
		bcc: [],
	});
	const sendEmail = useSendCommunicationEmail();
	const [subject, setSubject] = useState("");
	const [body, setBody] = useState("");
	const editor = useEditor({
		extensions: [StarterKit, Link],
		content: "",
		onUpdate: ({ editor: updatedEditor }) => setBody(updatedEditor.getHTML()),
	});
	const [selected, setSelected] = useState<string[]>([]);
	const parentRecipients = parentResolution.data?.to ?? [];
	const collegeStudentEmails = (collegeResolution.data?.to ?? []).filter((recipient) => recipient.kind === "student");
	const personalStudentEmails = (personalResolution.data?.to ?? []).filter((recipient) => recipient.kind === "student");
	const studentEmails = [...collegeStudentEmails, ...personalStudentEmails].filter(
		(recipient) => recipient.kind === "student",
	);
	const teachers = (teacherResolution.data?.to ?? []).filter(
		(recipient) => recipient.kind === "staff",
	);
	const recipientsLoading = collegeResolution.isLoading || personalResolution.isLoading || teacherResolution.isLoading || parentResolution.isLoading;
	const teacherLabels = useMemo(
		() =>
			new Map(
				(studentTeachers ?? []).map((teacher) => [
					teacher.initials.toLowerCase(),
					`${teacher.subjectName}${teacher.classCode ? ` ${teacher.classCode}` : ""}`,
				]),
			),
		[studentTeachers],
	);
	const recipientOptions = useMemo(
		() => [
			...studentEmails.map((r) => ({
				key: `student:${r.emailAddress}`,
				name: `${r.firstName} ${r.surname}`.trim(),
				email: r.emailAddress,
				studentNumbers: [studentNo], relationship: 'student',
			})),
			...teachers.map((r) => ({
				key: `staff:${r.emailAddress}`,
				name: `${r.firstName} ${r.surname}`.trim(),
				email: r.emailAddress,
				studentNumbers: [studentNo], relationship: 'teacher',
			})),
			...parentRecipients.map((r) => ({ key: `parent:${r.emailAddress}`, name: `${r.firstName} ${r.surname}`.trim(), email: r.emailAddress, studentNumbers: [studentNo], relationship: 'parent' })),
		],
		[studentEmails, teachers, parentRecipients],
	);
	const recipients = recipientOptions
		.filter((r) => selected.includes(r.key))
		.map((r) => ({ name: r.name, email: r.email, kind: "to" as const, studentNumbers: r.studentNumbers, relationship: r.relationship }));
	const htmlBody = `<!doctype html><html><body style="margin:0;padding:8px;font-family:Arial,Helvetica,sans-serif;color:#222">${body}</body></html>`;
	const toggleGroup = (items: { key: string }[], checked: boolean) =>
		setSelected((current) =>
			checked
				? [...new Set([...current, ...items.map((item) => item.key)])]
				: current.filter((key) => !items.some((item) => item.key === key)),
		);
	const send = async () => {
		if (
			!subject.trim() ||
			!editor?.getText().trim() ||
			recipients.length === 0
		) {
			notifications.show({
				color: "red",
				message: !recipients.length
					? "Select at least one recipient."
					: "Subject and body are required.",
			});
			return;
		}
		await sendEmail.mutateAsync({
			subject: subject.trim(),
			htmlBody,
			textBody: editor?.getText().trim(),
			recipients,
			isTest: false,
			deliveryPolicy: "per-student",
			source: "emails-home",
		});
		onClose();
	};
	return (
		<Tabs defaultValue="recipients">
			<Tabs.List mb="md">
				<Tabs.Tab value="recipients">Recipients ({recipients.length})</Tabs.Tab>
				<Tabs.Tab value="compose">Compose</Tabs.Tab>
				<Tabs.Tab value="preview">Preview</Tabs.Tab>
			</Tabs.List>
			<Tabs.Panel value="recipients">
				<ScrollArea.Autosize mah={560} type="auto" offsetScrollbars>
				<Stack gap="sm" pr="xs">
					<Text size="sm" fw={500}>
						{recipientsLoading
							? "Loading recipients…"
							: `${recipients.length} selected recipient${recipients.length === 1 ? "" : "s"}`}
					</Text>
					<Divider label="Students" labelPosition="left" />
                    <Stack gap={5}>
                        <Checkbox
                            label="College email"
                            checked={selected.some(
                                (key) =>
                                collegeStudentEmails.some((r) => key === `student:${r.emailAddress}`),
                            )}
                            onChange={(e) =>
                                toggleGroup(
                                    collegeStudentEmails
                                        .map((r) => ({ key: `student:${r.emailAddress}` })),
                                    e.currentTarget.checked,
                                )
                            }
                            size="xs"
                        />
					<Checkbox
						label="Personal email"
						disabled={personalStudentEmails.length === 0}
						checked={selected.some(
                                (key) =>
                                personalStudentEmails.some((r) => key === `student:${r.emailAddress}`),
                            )}
                            onChange={(e) =>
                                toggleGroup(
                                    personalStudentEmails
                                        .map((r) => ({ key: `student:${r.emailAddress}` })),
                                    e.currentTarget.checked,
                                )
                            }
                            size="xs"
                        />
                    </Stack>
					<Divider label="Teachers" labelPosition="left" />
                    <Stack gap={5}>
                        {teachers.map((r) => (
                            <Checkbox
                            size="xs"
                                key={r.emailAddress}
                                label={`${r.firstName} ${r.surname} (${teacherLabels.get(r.emailAddress.split("@")[0].toLowerCase()) ?? "Teacher"})`}
                                checked={selected.includes(`staff:${r.emailAddress}`)}
                                onChange={(e) =>
                                    toggleGroup(
                                        [{ key: `staff:${r.emailAddress}` }],
                                        e.currentTarget.checked,
                                    )
                                }
                            />
                        ))}
                    </Stack>
					<Divider label="Connected parents" labelPosition="left" />
                    <Stack gap={5}>
					{parentRecipients.map((r) => (
							<Checkbox
								key={r.emailAddress}
								label={`${r.firstName} ${r.surname}`}
								checked={selected.includes(`parent:${r.emailAddress}`)}
								onChange={(e) =>
									toggleGroup(
										[{ key: `parent:${r.emailAddress}` }],
										e.currentTarget.checked,
									)
								}
                                size="xs"
							/>
						))}
                    </Stack>
				</Stack>
				</ScrollArea.Autosize>
			</Tabs.Panel>
			<Tabs.Panel value="compose">
				<Stack gap="sm">
					<Text size="sm" fw={500}>{recipients.length} selected recipient{recipients.length === 1 ? "" : "s"}</Text>
					<TextInput
						label="Subject"
						value={subject}
						onChange={(e) => setSubject(e.currentTarget.value)}
						required
					/>
					<div>
						<Text size="sm" fw={500} mb={4}>Body</Text>
						<RichTextEditor editor={editor} styles={{ content: { minHeight: 180 } }}>
							<RichTextEditor.Toolbar>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.Bold />
									<RichTextEditor.Italic />
									<RichTextEditor.Underline />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.BulletList />
									<RichTextEditor.OrderedList />
								</RichTextEditor.ControlsGroup>
								<RichTextEditor.ControlsGroup>
									<RichTextEditor.Link />
									<RichTextEditor.Unlink />
									<RichTextEditor.ClearFormatting />
								</RichTextEditor.ControlsGroup>
							</RichTextEditor.Toolbar>
							<RichTextEditor.Content
								className={classes.editorContent}
							/>
						</RichTextEditor>
					</div>
					<Group justify="flex-end">
						<Button variant="default" onClick={onClose}>
							Cancel
						</Button>
						<Button
							loading={sendEmail.isPending}
							disabled={
								recipientsLoading ||
								recipients.length === 0
							}
							onClick={() => void send()}
						>
							Send email
						</Button>
					</Group>
				</Stack>
			</Tabs.Panel>
			<Tabs.Panel value="preview">
				<Stack gap="sm">
					<Text size="sm" fw={500}>
						{subject || "No subject"}
					</Text>
					<Box
						component="iframe"
						title="Email preview"
						srcDoc={htmlBody}
						w="100%"
						h={460}
						style={{
							border: "1px solid var(--mantine-color-default-border)",
							borderRadius: "var(--mantine-radius-md)",
						}}
					/>
				</Stack>
			</Tabs.Panel>
		</Tabs>
	);
}
